/**
 * RestoID — Sign-in with password.
 * ---------------------------------------------------------------------------
 * RestoID presentation on the PRISTINE upstream password sign-in logic:
 *   - submit ................ upstream `usePasswordSignIn`
 *   - forgot-password link .. upstream `ForgotPasswordLink` (settings-aware)
 *   - switch to code ........ upstream `SwitchToVerificationMethodsLink`
 *   - captcha ............... upstream `CaptchaBox`
 * Guards mirror the upstream page. Swapped in via `src/App.tsx`.
 */
import { SignInIdentifier } from '@logto/schemas';
import { cond } from '@silverhand/essentials';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import SwitchToVerificationMethodsLink from '@/components/SwitchToVerificationMethodsLink';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import { useForgotPasswordSettings, useSieMethods } from '@/hooks/use-sie';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';

import { Button, Field, Stack, ScreenHead } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

type FormProps = {
  identifier: SignInIdentifier;
  value: string;
  isVerificationCodeEnabled?: boolean;
};

const PasswordForm = ({ identifier, value, isVerificationCodeEnabled = false }: FormProps) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = usePasswordSignIn();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();

  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!password || submitting) {
      return;
    }
    clearErrorMessage();
    setIdentifierInputValue({ type: identifier, value });
    setSubmitting(true);
    try {
      await onSubmit({ identifier: { type: identifier, value }, password });
    } finally {
      setSubmitting(false);
    }
  }, [
    password,
    submitting,
    clearErrorMessage,
    setIdentifierInputValue,
    identifier,
    value,
    onSubmit,
  ]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Stack gap={18}>
        {/* Hidden identifier for password managers / autofill. */}
        <input readOnly hidden name="identifier" value={value} autoComplete={identifier} />
        <Field
          password
          label={t('input.password')}
          value={password}
          onChange={setPassword}
          danger={Boolean(errorMessage)}
          errorMessage={errorMessage}
        />
        {isForgotPasswordEnabled ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ForgotPasswordLink identifier={identifier} value={value} />
          </div>
        ) : null}
        <CaptchaBox />
        <Button
          htmlType="submit"
          title={t('action.sign_in')}
          isLoading={submitting}
          isDisabled={!password}
          onClick={() => void submit()}
        />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SwitchToVerificationMethodsLink
            hasVerificationCode={
              identifier !== SignInIdentifier.Username && isVerificationCodeEnabled
            }
            identifier={cond(identifier !== SignInIdentifier.Username && identifier)}
            value={value}
          />
        </div>
      </Stack>
    </form>
  );
};

const SignInPassword = () => {
  const c = useCopy();
  const { t } = useTranslation();
  const { signInMethods } = useSieMethods();
  const navigate = useNavigateWithPreservedSearchParams();
  const { identifierInputValue } = useContext(UserInteractionContext);

  if (!identifierInputValue) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = identifierInputValue;
  const methodSetting = signInMethods.find((method) => method.identifier === type);

  if (!methodSetting?.password) {
    return <ErrorPage />;
  }

  const subtitle =
    methodSetting.identifier === SignInIdentifier.Phone
      ? formatPhoneNumberWithCountryCallingCode(value)
      : value;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.enter_password' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.enter_password')}
          sub={subtitle}
        />
        <PasswordForm
          identifier={methodSetting.identifier}
          value={value}
          isVerificationCodeEnabled={methodSetting.verificationCode}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

export default SignInPassword;
